import { CustomTableConfig } from 'commons/components';
import { useTranslation } from 'core/i18n/i18n';
import { formatListField } from 'core/utils/format-table-cell';
import { EditableColumn } from 'modules/global/model';
import { useMemo } from 'react';
import { ROLE_LABEL_KEYS, USER_LOCATION_ASSIGNMENT_I18N_PREFIX } from '../settings-user-location-assignment.constants';
import { TenantUserRow } from '../settings-user-location-assignment.vm';

export const useTenantUsersTableConfig = () => {
    const { t } = useTranslation();

    return useMemo<CustomTableConfig<TenantUserRow>>(() => {
        const columnLabel = (column: string) => t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.columns.${column}`);

        const columnsToShow: EditableColumn[] = [
            { field: 'displayName', text: columnLabel('name'), visible: true, pinned: false, columnIndex: 0 },
            { field: 'role', text: columnLabel('role'), visible: true, pinned: false, columnIndex: 1 },
            { field: 'status', text: columnLabel('status'), visible: true, pinned: false, columnIndex: 2 },
            { field: 'locations', text: columnLabel('locations'), visible: true, pinned: false, columnIndex: 3 },
        ];

        // The gateway resolves the most-privileged role into `admin`, so a dual-role user renders the
        // admin label once instead of one entry per role (POC-20586 AC Group 2).
        const formatRole = (user: TenantUserRow) =>
            user.isAdmin
                ? t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.role.admin`)
                : user.roles
                      .map((role) =>
                          t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.role.${ROLE_LABEL_KEYS[role] ?? 'unknown'}`)
                      )
                      .join(', ');

        const formatStatus = (user: TenantUserRow) =>
            user.active
                ? t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.status.active`)
                : t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.status.inactive`);

        const formatLocations = (user: TenantUserRow) => {
            if (user.locations.length === 0) {
                return t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.locations.none`);
            }

            return formatListField(
                user.locations.map((location) => ({
                    id: location.id,
                    name: location.name ?? t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.locations.unknown`),
                }))
            );
        };

        return {
            entity: 'tenant-user',
            columnsToShow,
            fields: [
                {
                    name: 'displayName',
                    headerOptions: { text: columnLabel('name'), sortable: true },
                },
                {
                    name: 'role',
                    headerOptions: { text: columnLabel('role') },
                    rowOptions: { cellOptions: { format: formatRole } },
                },
                {
                    name: 'status',
                    headerOptions: { text: columnLabel('status') },
                    rowOptions: { cellOptions: { format: formatStatus } },
                },
                {
                    name: 'locations',
                    headerOptions: { text: columnLabel('locations') },
                    rowOptions: { cellOptions: { format: formatLocations } },
                },
            ],
        };
    }, [t]);
};
